/* eslint-disable react/prop-types */
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { MenuItem, TextField } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDCircularLoader from "components/MDCircularLoader";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DeleteModal from "examples/DeleteModal";
import PageShell from "components/common/PageShell";
import ListEmptyState from "components/common/ListEmptyState";
import StatusChip from "components/common/StatusChip";
import { ActionsCellRenderer } from "utils/helpers";
import DataTable from "../pr-management/components/DataTable";

import {
  FILTER_OPPORTUNITIES,
  UPDATE_OPPORTUNITY_STATUS,
} from "graphql/queries/filterOpportunities";
import { DELETE_OPPORTUNITY } from "graphql/mutations/deleteOpportunity";
import DeleteIcon from "assets/images/icons/deleteIcon.svg";
import { useState } from "react";

const STATUSES = ["OPEN", "IN_PROGRESS", "CLOSED"];

const STATUS_OPTIONS = [
  { value: "OPEN", label: "Open", background: "#FF9900" },
  { value: "IN_PROGRESS", label: "In Progress", background: "#439DEE" },
  { value: "CLOSED", label: "Closed", background: "#47BC4C" },
];

const cell = (value: any) => (
  <MDTypography sx={{ fontSize: "14px", fontWeight: 400 }}>
    {value ?? "-"}
  </MDTypography>
);

function Opportunities(): JSX.Element {
  const [status, setStatus] = useState<string>("");
  const { data, loading, refetch } = useQuery(FILTER_OPPORTUNITIES, {
    variables: {
      paginationInput: { currentPage: 0, perPage: 50 },
      status: status || undefined,
    },
    fetchPolicy: "network-only",
    onError: (err) => toast.error(err?.message || "Failed to load opportunities"),
  });
  const [updateStatus] = useMutation(UPDATE_OPPORTUNITY_STATUS);
  const [deleteOpportunity, { loading: deleting }] =
    useMutation(DELETE_OPPORTUNITY);
  const [deleteConf, setDeleteConf] = useState<string | null>(null);

  const onChangeStatus = async (id: string, value: string) => {
    try {
      await updateStatus({ variables: { id, status: value } });
      toast.success("Updated");
      refetch();
    } catch (err: any) {
      toast.error(err?.message || "Update failed");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteOpportunity({ variables: { id } });
      toast.success("Opportunity deleted");
      refetch();
    } catch (err: any) {
      toast.error(err?.message || "Delete failed");
    }
  };

  const rows = data?.filterOpportunitiesPaginated?.data || [];

  const columns = [
    {
      Header: "Channel",
      accessor: "channel",
      align: "left",
      Cell: ({ row }: any) => (
        <MDBox>
          {cell(row.original.channel)}
          <MDTypography variant="caption" color="text">
            {row.original.channelUserId}
          </MDTypography>
        </MDBox>
      ),
    },
    { Header: "Kind", accessor: "kind", Cell: ({ value }: any) => cell(value) },
    {
      Header: "Message",
      accessor: "text",
      align: "left",
      width: "35%",
      Cell: ({ value }: any) => (
        <MDTypography sx={{ fontSize: "14px", fontWeight: 400, whiteSpace: "normal" }}>
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Created",
      accessor: "createdAt",
      Cell: ({ value }: any) => cell(new Date(value).toLocaleDateString()),
    },
    {
      Header: "Status",
      accessor: "status",
      Cell: ({ row }: any) => (
        <StatusChip
          value={row.original.status}
          options={STATUS_OPTIONS}
          width={160}
          onChange={(val) => onChangeStatus(row.original.id, val)}
        />
      ),
    },
    {
      Header: "Actions",
      accessor: "id",
      align: "right",
      Cell: ({ row }: any) => (
        <ActionsCellRenderer
          actions={[
            { imgIcon: DeleteIcon, onClick: () => setDeleteConf(row.original.id) },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      {deleting && <MDCircularLoader overlayloader startLoader />}
      <DashboardNavbar />
      <PageShell
        filters={
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            <MDTypography variant="h5">Opportunities</MDTypography>
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </MDBox>
        }
      >
        {loading || rows.length ? (
          <DataTable
            loading={loading}
            table={{ columns, rows }}
            showHeader
            entriesPerPage={{ defaultValue: 25 }}
          />
        ) : (
          <ListEmptyState message="No opportunities found" />
        )}
      </PageShell>
      {deleteConf && (
        <DeleteModal
          onClose={() => setDeleteConf(null)}
          handleSubmit={() => handleDelete(deleteConf)}
        />
      )}
    </>
  );
}

export default Opportunities;
